import React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import useResponsive from '@/shared/hooks/useResponsive';

import ProgressLine from '@/features/laneChooser/components/progress/ProgressLine';
import ProgressTimer from '@/features/laneChooser/components/progress/ProgressTimer';
import AudioToggleButton from '@/shared/components/audioToggle/AudioToggleButton';
import BackButton from '@/shared/components/button/BackButton';

import '@/features/laneChooser/styles/lane-chooser-progress.scss';

export type LaneChooserProgressProps = {
	className?: string;
	currentIndex?: number;
	total?: number;
	answers?: boolean[];
	totalPlayTime?: number; // total seconds for the game
	gameStarted?: boolean; // when true, start ticking down
	timeAdjustSeconds?: number; // cumulative +/- seconds to add to remaining time
	onTimeOut?: () => void; // called once when timer hits 0
	onBack?: () => void; // override back behavior (used to mark abandoned)
};

const Progress = ({
	className,
	currentIndex,
	total = 0,
	answers = [],
	totalPlayTime,
	gameStarted,
	timeAdjustSeconds,
	onTimeOut,
	onBack,
}: LaneChooserProgressProps) => {
	const { t } = useTranslation();
	const { isMobile } = useResponsive();

	const safeTotal = Math.max(0, total);
	const remaining = Math.max(0, safeTotal - answers?.length);

	return (
		<div className={classNames('lane-chooser-progress', className)}>
			<div className="container">
				<div className="lane-chooser-progress__inner">
					<div className="lane-chooser-progress__controls">
						<BackButton onClick={() => onBack?.()} />
						{!isMobile && (
							<AudioToggleButton
								tooltip={t('lane_chooser.audio_toggle.tooltip')}
								className="lane-chooser-progress__audio-toggle"
							/>
						)}
					</div>

					<ProgressLine total={safeTotal} answers={answers} currentIndex={currentIndex} />

					<div className="lane-chooser-progress__info">
						<div className="lane-chooser-progress__remaining">
							{t('lane_chooser.remaining', { count: remaining })}
						</div>
						{isMobile && (
							<AudioToggleButton
								tooltip={t('lane_chooser.audio_toggle.tooltip')}
								className="lane-chooser-progress__audio-toggle"
								hiddenTooltip
							/>
						)}
						<ProgressTimer
							totalPlayTime={totalPlayTime}
							gameStarted={gameStarted}
							timeAdjustSeconds={timeAdjustSeconds}
							onTimeOut={onTimeOut}
						/>
					</div>
				</div>
			</div>
		</div>
	);
};

export default Progress;
